// packages/web/src/lib/hooks/use-preflight-credits.ts
/**
 * usePreflightCredits — client-side balance check before AI generation.
 *
 * Reads balance from account-slice; if insufficient, shows a toast with a
 * recharge CTA and returns false (caller aborts). This is a UX optimization —
 * the real enforcement is Gate 5 on the backend (CAS preflight). If the client
 * check passes but the server rejects (e.g. concurrent deduction), the server
 * returns 402 and the caller's error handler shows the message.
 *
 * Spec: docs/architecture/account-system-design.md §2.2, §9.2.4
 */

import { useStore } from '../store/index.js';
import { useToast } from '../../components/toast.js';
import { useT } from '../../i18n/use-t.js';

/**
 * Check if the user has enough credits for an operation costing `estPoints`.
 * Returns true if the operation should proceed, false if it should abort.
 *
 * - Anonymous users: balance is null → skip check (backend Gate 5 will 401).
 *   This lets the AuthModal flow handle the login prompt naturally.
 * - Authenticated with sufficient balance: deductLocally for instant UI feedback.
 * - Authenticated with insufficient balance: toast + return false.
 */
export function usePreflightCredits(): (estPoints: number) => boolean {
  const balance = useStore((s) => s.balance);
  const deductLocally = useStore((s) => s.deductLocally);
  const toast = useToast();
  const t = useT();

  return (estPoints: number): boolean => {
    // Anonymous — let backend handle (Gate 5 → 401 → AuthModal).
    if (balance === null) return true;
    if (balance < estPoints) {
      toast.warn(t('account.insufficientCredits'), {
        action: { label: t('account.recharge'), onClick: () => {
          // Phase 4 will wire /account?tab=recharge; for now just refresh.
          void useStore.getState().refreshBalance();
        } },
      });
      return false;
    }
    // Optimistic local deduction for instant UI feedback.
    // Backend Gate 5 is the truth; if it rejects, refreshBalance corrects.
    deductLocally(estPoints);
    return true;
  };
}

/** Estimated points per operation type (spec §2.1 · 1 point = ¥0.01). */
export const ESTIMATED_POINTS = {
  extractOutline: 24,
  fullIngest: 104,
  quickRegen: 6,
  deepRegen: 26,
  repair: 3,
  evaluate: 3,
  diagnose: 3,
  generateMock: 50,
} as const;
